import { useState } from 'react';
import { SvgPorterLogo } from '@kiper/ui';
import { useTranslation } from 'react-i18next';
import { useTheme } from 'styled-components';
import { useHistory } from 'react-router-dom';
import { useQuery as useQueryParams } from '@kiper/hooks';
import chroma from 'chroma-js';
import LoadingPage from '../../../components/LoadingPage';
import {
  Banner,
  CondominiumNotice,
  FaceRegisterActionArea,
  LogoCard,
  InfoContainer,
  EditFacialPhotoModal,
} from './components';
import { usePersonalizedInviteContext } from '../../../store/PersonalizedInvite/provider';
import { useFetchGuestFacePhoto } from './hooks';
import SmartLockCta from '../components/SmartLockCta';
import { INVITE_BRAND_VARIANTS } from '../constants';
import * as S from './styles';

const PersonalizedInviteContent = () => {
  const [t] = useTranslation('personalized-invite');
  const theme = useTheme();
  const history = useHistory();
  const querystring = useQueryParams();
  const guestHash = querystring?.get('q')?.replace(/ /g, '+') || '';
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const {
    inviteData,
    customization,
    eventInfo,
    location,
    hostMessage,
    condominiumNotice,
    loading,
    error,
    hasFacialRegistered,
    userName,
    previewFacePhotoUrl,
    hasSmartLock,
    isIOS,
    getPass,
    loadingPass,
    language,
  } = usePersonalizedInviteContext();

  const { facePhotoUrl: fetchedFacePhotoUrl } = useFetchGuestFacePhoto({
    guestHash,
    skip:
      !guestHash || !inviteData?.hasFacialRegistered || !!previewFacePhotoUrl,
  });
  const facePhotoUrl = previewFacePhotoUrl || fetchedFacePhotoUrl;

  const handleStartRegistration = () => {
    history.push(`/convite-personalizado/cadastro-facial?q=${guestHash}`);
  };

  const handleOpenEditModal = () => {
    if (!facePhotoUrl) {
      handleStartRegistration();
      return;
    }
    setIsEditModalOpen(true);
  };

  const handleCloseEditModal = () => setIsEditModalOpen(false);

  if (loading) return <LoadingPage />;

  if (error || !inviteData) {
    return <S.ErrorContainer>{t('invite-not-found')}</S.ErrorContainer>;
  }

  const textOnSecondaryColor =
    customization?.secondaryColor &&
    chroma.contrast(customization.secondaryColor, theme.colors.white) > 2.5
      ? theme.colors.white
      : theme.colors.porterPrimary;

  const infoTextColor =
    customization?.primaryColor &&
    chroma.contrast(customization.primaryColor, theme.colors.white) > 3.5
      ? customization.primaryColor
      : theme.colors.highBlack;

  const fallbackLanguage = ['pt', 'en', 'es'].includes(language)
    ? language
    : 'en';

  const inviteExpired = false;
  const inviteInvalid = false;

  return (
    <S.PageWrapper $secondaryColor={customization?.secondaryColor}>
      <S.InviteWrapper>
        <S.InviteContentWrapper secondaryColor={customization?.secondaryColor}>
          <S.Cover>
            <Banner
              bannerImageUrl={customization?.bannerImageUrl}
              bannerPosition={customization?.bannerPosition}
            />
            <LogoCard logoUrl={customization?.logoUrl} />
          </S.Cover>
          <FaceRegisterActionArea
            hasFacialRegistered={hasFacialRegistered}
            facialPhoto={facePhotoUrl}
            userName={userName}
            eventTitle={eventInfo?.title}
            hostMessage={hostMessage}
            headingColor={textOnSecondaryColor}
            primaryColor={customization?.primaryColor}
            onOpenEditModal={handleOpenEditModal}
          />
          {hasSmartLock && (
            <S.SmartLockCtaWrapper>
              <SmartLockCta
                guestHash={guestHash}
                variant={INVITE_BRAND_VARIANTS.PORTER}
              />
            </S.SmartLockCtaWrapper>
          )}
          <InfoContainer
            eventDate={eventInfo?.date}
            eventEndDate={eventInfo?.endDate}
            location={location}
            destination={eventInfo?.destination}
            primaryColor={infoTextColor}
          />
          {condominiumNotice && (
            <CondominiumNotice notice={condominiumNotice} />
          )}
          {!isIOS &&
            !(inviteExpired || inviteInvalid) &&
            inviteData?.qrCode && (
              <S.GoogleWalletButton
                className={loadingPass && 'disabled'}
                onClick={() => getPass()}
                lang={fallbackLanguage}
              />
            )}
          <S.Footer>
            <S.PoweredByText color={textOnSecondaryColor}>
              powered by
            </S.PoweredByText>
            <SvgPorterLogo size={68} color={textOnSecondaryColor} />
          </S.Footer>
        </S.InviteContentWrapper>
      </S.InviteWrapper>

      <EditFacialPhotoModal
        isOpen={isEditModalOpen}
        onClose={handleCloseEditModal}
        facialPhoto={facePhotoUrl}
        primaryColor={customization?.primaryColor}
      />
    </S.PageWrapper>
  );
};

export default PersonalizedInviteContent;
